import type { Point } from '../domain/model';
import { distanceKm, type Journey } from './routing';

// A compiled route is shared across display frames, not rebuilt for each SSE snapshot.
export interface VehicleTrack {
  points: Point[];
  seconds: number[];
  metres: number[];
  departure: number;
  end: number;
}
export function compileVehicleTrack(journey: Journey): VehicleTrack | undefined {
  if (journey.routeStatus !== 'ready' || !journey.geometry || journey.geometry.length < 2)
    return undefined;
  const points = journey.geometry;
  const departure = journey.departureAt ?? journey.start;
  const duration = Math.max(1, (journey.end - departure) / 1000);
  const seconds = [0],
    metres = [0];
  for (let i = 1; i < points.length; i++) {
    seconds.push(
      seconds[i - 1] + (journey.segmentSeconds?.[i - 1] ?? duration / (points.length - 1)),
    );
    metres.push(metres[i - 1] + distanceKm(points[i - 1], points[i]) * 1000);
  }
  return { points, seconds, metres, departure, end: journey.end };
}
function segmentAt(values: number[], value: number) {
  let lo = 0,
    hi = values.length - 1;
  // Skip zero-length/time segments, including repeated routing coordinates.
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (values[mid] <= value) lo = mid;
    else hi = mid - 1;
  }
  return Math.min(lo, values.length - 2);
}
function interpolate(a: Point, b: Point, fraction: number): Point {
  return [a[0] + (b[0] - a[0]) * fraction, a[1] + (b[1] - a[1]) * fraction];
}
function pointAtDistance(track: VehicleTrack, metres: number): Point {
  const distance = Math.max(0, Math.min(track.metres.at(-1)!, metres));
  const i = segmentAt(track.metres, distance);
  const length = track.metres[i + 1] - track.metres[i];
  return interpolate(
    track.points[i],
    track.points[i + 1],
    length > 0 ? (distance - track.metres[i]) / length : 1,
  );
}
export function mapBearing(from: Point, to: Point): number | undefined {
  const x = (to[1] - from[1]) * Math.cos((((from[0] + to[0]) / 2) * Math.PI) / 180);
  const y = to[0] - from[0];
  if (Math.abs(x) + Math.abs(y) < 1e-12) return undefined;
  return ((Math.atan2(x, y) * 180) / Math.PI + 360) % 360;
}
export function continuousHeading(previous: number | undefined, target: number) {
  return previous === undefined
    ? target
    : previous + ((((target - previous + 540) % 360) + 360) % 360) - 180;
}
export function sampleVehicleTrack(track: VehicleTrack, now: number) {
  const elapsed = Math.max(0, Math.min(track.seconds.at(-1)!, (now - track.departure) / 1000));
  const segment = segmentAt(track.seconds, elapsed);
  const duration = track.seconds[segment + 1] - track.seconds[segment];
  const fraction = duration > 0 ? (elapsed - track.seconds[segment]) / duration : 1;
  const position = interpolate(track.points[segment], track.points[segment + 1], fraction);
  const distance =
    track.metres[segment] + (track.metres[segment + 1] - track.metres[segment]) * fraction;
  // Only orientation looks a few metres ahead/behind. The position never cuts a corner.
  const heading = mapBearing(
    pointAtDistance(track, distance - 3),
    pointAtDistance(track, distance + 3),
  );
  return { position, heading, segment };
}
export function displaySimulationTime(
  snapshot: { now: number; running: boolean; speed: number },
  receivedAt: number,
  frameAt: number,
) {
  // At most one server tick of prediction; disconnected clients cannot drive indefinitely.
  return (
    snapshot.now +
    (snapshot.running ? Math.max(0, Math.min(1000, frameAt - receivedAt)) * snapshot.speed : 0)
  );
}
